'use client'

import Image from 'next/image'
import { motion, useReducedMotion } from 'framer-motion'

/**
 * Soft background for program pages: drifting colour glows + faint Japanese
 * characters that float slowly. Purely decorative; sits behind the content.
 * Parent needs `relative` (and ideally `overflow-hidden`).
 */
const KANA = ['あ', '日', 'に', '本', 'ほ', '語', 'ん', 'さ', 'く', 'ら', '学', 'ゆ']

export function PageDecor() {
  const reduced = useReducedMotion()
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 -z-0 overflow-hidden">
      <motion.div
        className="absolute -top-16 -left-20 h-80 w-80 rounded-full bg-brand-green-soft opacity-60 blur-3xl"
        animate={reduced ? undefined : { x: [0, 30, 0], y: [0, -20, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute top-10 -right-24 h-96 w-96 rounded-full bg-brand-red-soft opacity-50 blur-3xl"
        animate={reduced ? undefined : { x: [0, -30, 0], y: [0, 20, 0] }}
        transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
      />
      {KANA.map((k, i) => (
        <motion.span
          key={i}
          className="absolute select-none font-bold text-brand-charcoal/[0.045]"
          style={{
            left: `${(i * 37 + 5) % 92}%`,
            top: `${(i * 23 + 4) % 60}%`,
            fontSize: `${40 + ((i * 13) % 52)}px`,
          }}
          animate={reduced ? undefined : { y: [0, -14, 0], rotate: [0, i % 2 ? 4 : -4, 0] }}
          transition={{ duration: 7 + (i % 4), repeat: Infinity, ease: 'easeInOut', delay: i * 0.4 }}
        >
          {k}
        </motion.span>
      ))}
    </div>
  )
}

/**
 * Picture strip images. Currently illustrations; to use AI photos instead,
 * save them over these files with the same names (and the large versions without "-sm").
 */
const CLASSROOM_PHOTOS = [
  { src: '/images/classroom/class-hiragana-sm.webp', alt: 'Japanese classroom with hiragana on the board' },
  { src: '/images/classroom/class-lecture-sm.webp', alt: 'Mt. Fuji, torii gate and cherry blossoms' },
  { src: '/images/classroom/instructor-front-sm.webp', alt: 'Calligraphy scroll reading Nihongo' },
  { src: '/images/classroom/class-discussion-sm.webp', alt: 'Students practising Japanese conversation' },
  { src: '/images/classroom/class-screen-sm.webp', alt: 'Online Japanese lesson on a laptop' },
]

/** Row of real classroom photos, slightly tilted, that lift on hover. */
export function ClassroomPhotoStrip({ className = '' }: { className?: string }) {
  const reduced = useReducedMotion()
  return (
    <div className={`relative ${className}`}>
      <div className="flex gap-3 overflow-x-auto pb-2 sm:justify-center sm:gap-4 [scrollbar-width:none]">
        {CLASSROOM_PHOTOS.map((p, i) => (
          <motion.div
            key={p.src}
            initial={reduced ? false : { opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            whileHover={reduced ? undefined : { y: -6, rotate: 0, scale: 1.03 }}
            className={`relative h-32 w-44 shrink-0 overflow-hidden rounded-2xl shadow-lg ring-4 ring-white sm:h-36 sm:w-52 ${
              i % 2 ? 'rotate-2' : '-rotate-2'
            }`}
          >
            <Image src={p.src} alt={p.alt} fill sizes="208px" className="object-cover" />
          </motion.div>
        ))}
      </div>
    </div>
  )
}
